import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "60 Minute Massage",
  "90 Minute Massage",
  "The Long Restoration — 120 Minute Massage",
  "The Signature Glow Facial",
  "The Grand Ritual Facial",
  "The Renewal Peel",
  "The Velvet Dermaplane",
  "Other / general question",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const messageRef = useRef(null);

  useEffect(() => {
    const onPrefill = (e) => {
      const message = e?.detail?.message;
      const service = e?.detail?.service;
      setForm((f) => ({
        ...f,
        ...(typeof message === "string" && message.length ? { message } : {}),
        ...(typeof service === "string" && service.length ? { service } : {}),
      }));
      if (typeof message === "string" && message.length) {
        setTimeout(() => {
          messageRef.current?.focus();
          messageRef.current?.setSelectionRange?.(message.length, message.length);
        }, 400);
      }
    };
    window.addEventListener("prefill-contact", onPrefill);
    return () => window.removeEventListener("prefill-contact", onPrefill);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend /contact accepts {name,email,phone,message} — fold service into message
      const composed = form.service
        ? `Service requested: ${form.service}\n\n${form.message}`
        : form.message;
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: composed,
      });
      toast.success("Message sent.", {
        description: "Bella will be in touch shortly. Thank you for reaching out.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Please try again or call us directly.";
      toast.error("Could not send message", { description: typeof msg === "string" ? msg : "Validation error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-36 bg-[#FAF6E8]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 md:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <Reveal className="overline mb-6">Visit / write / call</Reveal>
          <Reveal delay={1}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light bb-ink">
              Let&rsquo;s find a <br />
              <span className="italic text-[#C9A65A]">quiet hour</span> for you.
            </h2>
          </Reveal>
          <Reveal delay={2} className="mt-8 bb-ink-soft text-base sm:text-lg leading-relaxed max-w-md font-light">
            <p>
              Questions about a service, gifting, or finding the right session?
              Send a note — Bella reads each one personally.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            <Reveal delay={3} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#F4ECC9] flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 bb-ink" />
              </div>
              <div>
                <div className="overline mb-1">Phone</div>
                <a
                  href="tel:9162435514"
                  data-testid="contact-phone-link"
                  className="font-serif text-2xl bb-ink link-underline text-link-hover"
                >
                  916-243-5514
                </a>
              </div>
            </Reveal>
            <Reveal delay={4} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#EAD584] flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 bb-ink" />
              </div>
              <div>
                <div className="overline mb-1">Email</div>
                <a
                  href="mailto:Bella@triplebtherapy.com"
                  data-testid="contact-email-link"
                  className="font-serif text-2xl bb-ink link-underline text-link-hover break-all"
                >
                  Bella@triplebtherapy.com
                </a>
              </div>
            </Reveal>
            <Reveal delay={5} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#F2EBD3] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 bb-ink" />
              </div>
              <div>
                <div className="overline mb-1">Location</div>
                <div className="font-serif text-2xl bb-ink">Downtown Sacramento</div>
                <div className="bb-ink-soft text-sm font-light">Serving Sacramento &amp; surrounding areas</div>
              </div>
            </Reveal>
            <Reveal delay={5} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#F4ECC9] flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 bb-ink" />
              </div>
              <div data-testid="studio-hours">
                <div className="overline mb-1">Studio hours</div>
                <ul className="bb-ink text-base font-light leading-relaxed">
                  <li><span className="inline-block w-24 bb-ink-soft">Monday</span>10am &ndash; 5pm</li>
                  <li><span className="inline-block w-24 bb-ink-soft">Tuesday</span>11am &ndash; 2pm</li>
                  <li><span className="inline-block w-24 bb-ink-soft">Wednesday</span>11am &ndash; 2pm</li>
                  <li><span className="inline-block w-24 bb-ink-soft">Thursday</span>11am &ndash; 2pm</li>
                  <li className="bb-ink-soft italic text-sm mt-1">Fri &ndash; Sun &middot; closed</li>
                </ul>
                <div className="bb-ink-soft text-xs font-light mt-3 max-w-xs leading-relaxed">
                  In-home / mobile sessions are available by request &mdash; share your details below and Bella will follow up.
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={2} className="col-span-12 lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-[#F2EBD3] border border-[#D9CFA0] rounded-lg p-6 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="overline block mb-2">Name</span>
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  data-testid="contact-name-input"
                  className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-3 text-base bb-ink font-light"
                  placeholder="Your name"
                />
              </label>
              <label className="block">
                <span className="overline block mb-2">Email</span>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="contact-email-input"
                  className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-3 text-base bb-ink font-light"
                  placeholder="you@email.com"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="overline block mb-2">Phone</span>
                <input
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="contact-phone-input"
                  className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-3 text-base bb-ink font-light"
                  placeholder="(916) 000-0000"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="overline block mb-2">Service interested in</span>
                <select
                  value={form.service}
                  onChange={set("service")}
                  data-testid="contact-service-select"
                  className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-3 text-base bb-ink font-light appearance-none"
                >
                  <option value="">Select a service (optional)</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span className="mt-2 inline-block text-xs italic bb-gold font-light">
                  Facials are now open — Signature Glow, Grand Ritual, Renewal Peel &amp; Velvet Dermaplane.
                </span>
              </label>
              <label className="block md:col-span-2">
                <span className="overline block mb-2">Message</span>
                <textarea
                  required
                  rows={5}
                  ref={messageRef}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message-input"
                  className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-3 text-base bb-ink font-light resize-none"
                  placeholder="What are you hoping for? (Mention if you'd like an in-home session — include address city/zip and a couple of date/time options.)"
                />
              </label>
            </div>
            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="bb-ink-soft text-xs max-w-sm font-light">
                By submitting you agree to be contacted by Beauty &amp; Balance.
                We never share your information.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-shine inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm tracking-wider uppercase text-[#FAF6E8] bg-[#0F1F4E] hover:bg-[#1B3370] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"}
                {!loading && <Send className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
